import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createTokenManager } from './tokenManager';
import { 
  RequestConfig, 
  ApiResponse, 
  ApiRequestOptions, 
  RefreshApiResponse,
  ITokenManager 
} from './types';

/**
 * 创建请求实例的工厂函数
 */
export function createRequest(config: RequestConfig) {
  const {
    baseURL = '',
    timeout = 30000,
    loginPath,
    enableTokenExpiration = true,
    enable402Redirect = false,
    shopAuthorizationPath = '/authorization/shop-authorization',
    supportExternalRefresh = false,
    mockMap,
    enableMock = false,
  } = config;

  // 创建 Token 管理器
  const TokenManager = createTokenManager(enableTokenExpiration);

  // 外部刷新 Token 处理函数（用于微前端场景）
  let externalRefreshTokenHandler: (() => Promise<string | null>) | null = null;

  /**
   * 设置外部刷新 Token 处理函数
   */
  const setExternalRefreshTokenHandler = (handler: () => Promise<string | null>) => {
    externalRefreshTokenHandler = handler;
  };

  /**
   * 尝试刷新 Token
   */
  const performTokenRefreshAttempt = async (): Promise<string | null> => {
    // 如果支持外部刷新且配置了外部刷新处理函数（如微前端场景下的主应用刷新方法），优先使用
    if (supportExternalRefresh && externalRefreshTokenHandler) {
      console.log('[Token Refresh] 使用外部刷新处理函数 (Host)');
      try {
        const newToken = await externalRefreshTokenHandler();
        if (newToken) {
          // 更新本地 TokenManager
          TokenManager.setAccessToken(newToken);
          console.log('[Token Refresh] 外部刷新成功，更新本地 token');
          return newToken;
        }
        return null;
      } catch (error) {
        console.error('[Token Refresh] 外部刷新失败:', error);
        return null;
      }
    }

    try {
      const refreshTokenValue = TokenManager.getRefreshToken();
      console.log('[Token Refresh] 调用刷新接口 /api/user/v2/auth/refresh');
      
      // 调用刷新 token 的 API - 使用原始的 fetch 方法，避免循环依赖
      const response = await fetch('/api/user/v2/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue,
        }),
      });
      
      console.log('[Token Refresh] 刷新接口响应状态:', response.status);
      const data: RefreshApiResponse = await response.json();
      console.log('[Token Refresh] 刷新接口响应数据:', data);

      if (data.code === 200) {
        const result = data.data;
        if (result?.access_token) {
          TokenManager.setAccessToken(result.access_token);

          if (result.access_expires) {
            TokenManager.setAccessTokenExpires(result.access_expires);
          }
          // 如果返回了新的 refresh_token 和过期时间，更新它们
          if (result.refresh_token) {
            TokenManager.setRefreshToken(result.refresh_token);
          }
          if (result.refresh_expires) {
            TokenManager.setRefreshTokenExpires(result.refresh_expires);
          }
          console.log('[Token Refresh] Token 刷新成功');
          return result.access_token;
        }
      }
      
      // 如果返回 401，说明刷新令牌无效，清除所有令牌并跳转到登录页
      if (data.code === 401) {
        console.log('[Token Refresh] refresh_token 无效，清除所有 token');
        TokenManager.clearAllTokensAndUserInfo();
        window.location.href = loginPath;
        return null;
      }
      
      console.log('[Token Refresh] 刷新失败，返回 code:', data.code);
      return null;
    } catch (error) {
      console.error('[Token Refresh] 刷新 token 异常:', error);
      return null;
    }
  };

  /**
   * 刷新 token 的方法
   */
  const refreshToken = async (): Promise<string | null> => {
    // 检查 refresh_token 是否存在
    const refreshTokenValue = TokenManager.getRefreshToken();
    if (!refreshTokenValue) {
      console.log('[Token Refresh] refresh_token 不存在');
      return null;
    }

    // 检查 refresh_token 是否过期
    if (TokenManager.isRefreshTokenExpired()) {
      console.log('[Token Refresh] refresh_token 已过期');
      return null;
    }

    console.log('[Token Refresh] 开始刷新 token...');
    return performTokenRefreshAttempt();
  };

  // 创建 axios 实例
  const request: AxiosInstance = axios.create({
    baseURL,
    timeout,
  });

  // 请求拦截器 - 添加认证头
  request.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Mock 数据处理（仅开发环境）
      // @ts-ignore - import.meta.env 在 Vite 环境下可用
      if (enableMock && mockMap && typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        const url = config.url || '';
        const pureUrl = url.replace(config.baseURL || '', '');
        
        if (mockMap[pureUrl]) {
          console.log(`[Mock Interceptor] Matching URL: ${pureUrl}`);
          config.adapter = (_config: any) => {
            return new Promise((resolve) => {
              const res = {
                data: mockMap[pureUrl],
                status: 200,
                statusText: 'OK',
                headers: {},
                config: _config,
                request: {}
              };
              resolve(res as any);
            });
          };
        }
      }

      const accessToken = TokenManager.getAccessToken();
      const isGuestMode = localStorage.getItem('isGuestMode');

      // 如果有 access_token，添加到 Authorization 头
      if (accessToken) {
        config.headers.Authorization = `bearer ${accessToken}`;
      }

      if (isGuestMode === 'true') {
        config.headers['X-Demo-Mode'] = 'true';
      }

      return config;
    },
    (error: Error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器 - 处理错误和 token 刷新
  request.interceptors.response.use(
    (response: any) => {
      // 处理 API 返回的错误格式 {code: xxx, message: "...", data: null}
      const data = response.data;
      if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
        if (data.code && data.code !== 200) {
          // 创建一个错误对象
          const error: any = new Error(data.message);
          error.name = 'APIError';
          error.code = data.code;
          error.message = data.message;
          error.response = {
            status: data.code,
            data: data
          };
          return Promise.reject(error);
        }
      }
      return response;
    },
    async (error: AxiosError) => {
      // 如果是 401 或 403 错误，尝试刷新 token
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log(`[Token Refresh] 检测到 ${error.response?.status} 错误，尝试刷新 token...`);
        const newToken = await refreshToken();
        if (newToken && error.config) {
          // Token refreshed, retry the original request
          console.log('[Token Refresh] Token 刷新成功，重试原始请求');
          error.config.headers.Authorization = `bearer ${newToken}`;
          return request(error.config);
        } else {
          // Token 刷新失败，清除所有 token 并跳转到登录页
          console.log('[Token Refresh] Token 刷新失败，跳转到登录页');
          TokenManager.clearAllTokensAndUserInfo();
          window.location.href = loginPath;
          return new Promise(() => { });
        }
      } else if (enable402Redirect && error.response?.status === 402) {
        // 跳转到店铺授权页面
        window.location.href = shopAuthorizationPath;
        return new Promise(() => { });
      }
      return Promise.reject(error);
    }
  );

  /**
   * 封装请求方法
   */
  async function apiRequest<T = any>(
    url: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { method = 'GET', data, params } = options;

    const response = await request({
      url,
      method,
      data,
      params,
    });

    return response.data;
  }

  return {
    request,
    apiRequest,
    TokenManager,
    setExternalRefreshTokenHandler,
    performTokenRefreshAttempt,
  };
}
